import React, { useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import style from './WeeklyDateSwiper.module.css';

const KOR_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKS_BACK = 52;

const WeeklyDateSwiper = ({ selectedDate, onSelectDate, dayMarks = {} }) => {
    const today = useMemo(() => dayjs().startOf('day'), []);
    const swiperRef = useRef(null);

    const slides = useMemo(() => {
        const anchor = selectedDate.startOf('day');
        const arr = [];

        const buildSlide = (end) => ({
            end,
            days: Array.from({ length: 7 }, (_, idx) => end.subtract(6 - idx, 'day')),
        });

        // 과거 51주 + selectedDate 슬라이드 (총 52개, selectedDate가 마지막의 우측 끝)
        for (let i = WEEKS_BACK - 1; i >= 0; i--) {
            arr.push(buildSlide(anchor.subtract(i * 7, 'day')));
        }

        // 미래 슬라이드: anchor + 7, +14, ... 슬라이드 시작일이 today 이하인 동안 push
        let cursor = anchor.add(7, 'day');
        while (!cursor.subtract(6, 'day').isAfter(today, 'day')) {
            arr.push(buildSlide(cursor));
            cursor = cursor.add(7, 'day');
            if (arr.length > WEEKS_BACK + 60) break; // 안전장치
        }

        return arr;
    }, [selectedDate, today]);

    // selectedDate가 우측 끝인 슬라이드 인덱스 = 항상 WEEKS_BACK - 1
    const initialIdx = WEEKS_BACK - 1;
    // 날짜 변경 시 Swiper 재마운트 → initialSlide로 즉시 점프
    const swiperKey = selectedDate.format('YYYYMMDD');

    return (
        <div className={style.wrap}>
            <h2 className={style.dateTitle}>{selectedDate.format('YY년 M월 D일')}</h2>

            <Swiper
                key={swiperKey}
                slidesPerView={1}
                initialSlide={initialIdx}
                speed={300}
                onSwiper={(s) => { swiperRef.current = s; }}
                touchStartPreventDefault={false}
                resistanceRatio={0.5}
            >
                {slides.map((slide, sIdx) => (
                    <SwiperSlide key={sIdx}>
                        <ul className={style.dayRow}>
                            {slide.days.map((d) => {
                                const isToday = d.isSame(today, 'day');
                                return (
                                    <li
                                        key={`dow-${d.format('YYYYMMDD')}`}
                                        className={isToday ? style.todayText : ''}
                                    >
                                        {KOR_DAYS[d.day()]}
                                    </li>
                                );
                            })}
                        </ul>
                        <ul className={style.dateRow}>
                            {slide.days.map((d) => {
                                const key = d.format('YYYYMMDD');
                                const isToday = d.isSame(today, 'day');
                                const isSelected = d.isSame(selectedDate, 'day');
                                const isFuture = d.isAfter(today, 'day');
                                const mark = dayMarks[key];

                                let btnClass = '';
                                if (isSelected) {
                                    btnClass = isToday ? style.circleBlue : style.circleBlack;
                                } else if (isToday) {
                                    btnClass = style.todayText;
                                }
                                if (isFuture) btnClass += ' ' + style.disabled;

                                return (
                                    <li key={`date-${key}`}>
                                        <button
                                            type="button"
                                            className={`${style.dateBtn} ${btnClass}`}
                                            onClick={() => !isFuture && onSelectDate(d)}
                                            disabled={isFuture}
                                            aria-label={`${d.format('M월 D일')}${isToday ? ' 오늘' : ''}${isSelected ? ' 선택됨' : ''}`}
                                            aria-pressed={isSelected}
                                        >
                                            {d.date()}
                                        </button>
                                        <span
                                            className={`${style.dot} ${
                                                mark === 'warn' ? style.dotWarn :
                                                mark === 'good' ? style.dotGood : ''
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default WeeklyDateSwiper;